import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CreditCard, Plus, Building2, Link2, Wallet, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { usePaymentMethods } from '../hooks/use-billing-data';
import { usePaymentPortal } from '../hooks/use-billing-mutations';
import {
  getPaymentMethodIconType,
  formatPaymentMethodText,
  isPaymentMethodExpired,
  isPaymentMethodExpiringSoon,
} from './utils';
import { useOrgSlug } from '@/hooks/use-org-slug';
import { usePaymentStatus } from '@/hooks/use-payment-status';
import { ORG_ROUTES } from '@/config/router';
import { toast } from 'sonner';
import { VaultCardDialog } from './VaultCardDialog';
import { CARD_SETUP_RETURN_ERRORS, readCardSetupReturn } from '@/lib/billing-card-setup-return';

export function PaymentMethodsSection() {
  const { t, i18n } = useTranslation('billing');
  const { paymentMethods, billingProvider, isLoadingPaymentMethods } = usePaymentMethods();
  const openPaymentPortalMutation = usePaymentPortal();
  const { urlSlug } = useOrgSlug();
  const [vaultCardOpen, setVaultCardOpen] = useState(false);
  const { pendingProvider } = usePaymentStatus();
  // An account manager is moving this account to a vault-billed provider:
  // the current card keeps being charged until a card is saved there.
  const awaitingVaultCard = Boolean(pendingProvider && pendingProvider !== 'stripe');

  // Back from the bank's 3-D Secure page after adding a card here.
  useEffect(() => {
    const outcome = readCardSetupReturn();
    if (outcome === 'verified') toast.success('Your card is saved.');
    else if (outcome) toast.error(CARD_SETUP_RETURN_ERRORS[outcome]);
  }, []);

  const handleOpenPaymentPortal = () => {
    // Vault-billed accounts (Checkout.com) have no hosted portal: the card
    // form opens here.
    if (billingProvider && billingProvider !== 'stripe') {
      setVaultCardOpen(true);
      return;
    }
    if (!urlSlug) {
      console.error('No organization slug found - cannot construct return URL');
      toast.error(t('toasts.orgNotFoundOpenPaymentPortal'));
      return;
    }

    const returnUrl = `${window.location.origin}${ORG_ROUTES(urlSlug).BILLING()}?tab=payment-methods`;
    openPaymentPortalMutation.mutate({ returnUrl });
  };

  const renderPaymentMethodIcon = (iconType: string, pm: any) => {
    const iconClasses = 'h-6 w-6';
    const isExpired = isPaymentMethodExpired(pm);
    const isExpiringSoon = isPaymentMethodExpiringSoon(pm);
    const iconColor = isExpired ? 'text-red-500' : isExpiringSoon ? 'text-yellow-500' : 'text-blue-500';

    switch (iconType) {
      case 'card':
        return <CreditCard className={`${iconClasses} ${iconColor}`} />;
      case 'bank':
        return <Building2 className={`${iconClasses} ${iconColor}`} />;
      case 'link':
        return <Link2 className={`${iconClasses} ${iconColor}`} />;
      case 'wallet':
        return <Wallet className={`${iconClasses} ${iconColor}`} />;
      default:
        return <CreditCard className={`${iconClasses} ${iconColor}`} />;
    }
  };

  const renderStatusBadge = (pm: any) => {
    const isExpired = isPaymentMethodExpired(pm);
    const isExpiringSoon = isPaymentMethodExpiringSoon(pm);

    if (isExpired) {
      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {t('paymentMethods.badges.expired')}
        </Badge>
      );
    }

    if (isExpiringSoon) {
      return (
        <Badge variant="yellow" className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {t('paymentMethods.badges.expiringSoon')}
        </Badge>
      );
    }

    if (pm.isDefault) {
      return (
        <Badge variant="green" className="flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          {t('paymentMethods.badges.default')}
        </Badge>
      );
    }

    return null;
  };

  const dateLocale = i18n.language?.startsWith('fr') ? 'fr-FR' : 'en-US';

  return (
    <div>
      {awaitingVaultCard && (
        <div className="mb-4 flex flex-col gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-amber-900">
            We are moving your TagadaPay billing to a new payment partner. Please add your card once more;
            your current card keeps working until then.
          </p>
          <Button size="sm" onClick={() => setVaultCardOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add card
          </Button>
        </div>
      )}

      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{t('paymentMethods.title')}</h3>
          <p className="text-muted-foreground text-sm">{t('paymentMethods.description')}</p>
        </div>
        {paymentMethods.length > 0 && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleOpenPaymentPortal}
            disabled={openPaymentPortalMutation.isPending}
          >
            {t('paymentMethods.manage')}
          </Button>
        )}
      </div>

      {isLoadingPaymentMethods ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
        </div>
      ) : paymentMethods.length > 0 ? (
        <div className="space-y-3">
          {paymentMethods.map((pm: any) => {
            const displayText = formatPaymentMethodText(pm, t);
            const iconType = getPaymentMethodIconType(pm.type);
            const isExpired = isPaymentMethodExpired(pm);
            const isExpiringSoon = isPaymentMethodExpiringSoon(pm);
            const hasWarning = isExpired || isExpiringSoon;

            return (
              <div
                key={pm.id}
                className={`rounded-lg border p-4 transition-all ${hasWarning ? 'border-yellow-300 bg-yellow-50/50' : 'bg-card hover:bg-accent/5'} ${isExpired ? 'border-red-300 bg-red-50/50' : ''} `}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="bg-muted flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg">
                      {renderPaymentMethodIcon(iconType, pm)}
                    </div>

                    <div className="flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-base font-semibold">{displayText.primary}</p>
                        {renderStatusBadge(pm)}
                      </div>

                      <p className="text-muted-foreground text-sm">{displayText.secondary}</p>

                      {displayText.tertiary && (
                        <p className="text-muted-foreground text-xs">{displayText.tertiary}</p>
                      )}

                      {pm.createdAt && (
                        <p className="text-muted-foreground text-xs">
                          {t('paymentMethods.added')}{' '}
                          {new Date(pm.createdAt).toLocaleDateString(dateLocale, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {hasWarning && (
                  <div
                    className={`mt-3 flex items-start gap-2 rounded-md p-3 ${
                      isExpired ? 'bg-red-100' : 'bg-yellow-100'
                    }`}
                  >
                    <AlertCircle
                      className={`mt-0.5 h-4 w-4 flex-shrink-0 ${
                        isExpired ? 'text-red-600' : 'text-yellow-600'
                      }`}
                    />
                    <div className="text-sm">
                      <p className={`font-medium ${isExpired ? 'text-red-900' : 'text-yellow-900'}`}>
                        {isExpired
                          ? t('paymentMethods.warnings.expiredTitle')
                          : t('paymentMethods.warnings.expiringSoonTitle')}
                      </p>
                      <p className={`mt-1 text-xs ${isExpired ? 'text-red-700' : 'text-yellow-700'}`}>
                        {t('paymentMethods.warnings.updateHint')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border-2 border-dashed py-12 text-center">
          <div className="bg-muted mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full">
            <CreditCard className="text-muted-foreground h-8 w-8" />
          </div>
          <h3 className="mb-2 text-base font-semibold">{t('paymentMethods.empty.title')}</h3>
          <p className="text-muted-foreground mb-6 text-sm">{t('paymentMethods.empty.description')}</p>
          <Button
            size="default"
            onClick={handleOpenPaymentPortal}
            disabled={openPaymentPortalMutation.isPending}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('paymentMethods.add')}
          </Button>
        </div>
      )}
      <VaultCardDialog
        open={vaultCardOpen}
        onOpenChange={setVaultCardOpen}
        forPendingProvider={awaitingVaultCard}
      />
    </div>
  );
}
